import { ChatHistoryTable } from '@/app/chat-history/chat-history'
import { Chat } from '@/app/chat/chat'
import { DataGovernanceDashboard } from '@/app/data-catalog/dashboard'
import { DataCatalogTable } from '@/app/data-catalog/data-catalog'
import { IndustryReferenceTable } from '@/app/data-catalog/industry-reference-table'
import { NewChat } from '@/app/new-chat/new-chat'
import { AgentRegistryTable } from '@/app/registry/agent-registry-table'
import { ServiceRegistryTab } from '@/app/registry/service-registry/tab'
import { ScheduleTable } from '@/app/schedule/schedule'
import { Settings } from '@/app/settings/settings'
import React from 'react'
import {
  Tab,
  TAB_TYPE_CHAT,
  TAB_TYPE_DATA_GOVERNANCE_DASHBOARD,
  TAB_TYPE_DATABASE_METADATA,
  TAB_TYPE_DATABASE_SCHEMA,
  TAB_TYPE_DATABASE_TABLE,
  TAB_TYPE_TURN,
  TabTypeStatic,
  TabTypeTurnStep,
} from '../types'
import { BlankTabContent } from './blank-tab'
import {
  DatabaseMetadataTabContent,
  DatabaseSchemaTabContent,
  DatabaseTableTabContent,
} from './database-tabs'
import {
  LiveSessionTabContent,
  RecordedSessionTabContent,
  StepUpdatesTabContent,
} from './turn-step-tabs'
import { TurnOutputTabContent } from './turn-tabs'

// =============================================================================
// CONTENT RENDERER FUNCTION
// =============================================================================

/**
 * Renders the appropriate content component based on the tab type
 * This function provides a type-safe way to render tab content
 */
export const renderTabContent = (tab: Tab): React.ReactElement => {
  switch (tab.type) {
    // Static tabs with special handling
    case TabTypeStatic.Blank:
      return <BlankTabContent tabId={tab.id} />
    case TabTypeStatic.History:
      return <ChatHistoryTable />
    case TabTypeStatic.AgentRegistry:
      return <AgentRegistryTable />
    case TabTypeStatic.ServiceRegistry:
      return <ServiceRegistryTab />
    case TabTypeStatic.Schedule:
      return <ScheduleTable />
    case TabTypeStatic.DataCatalog:
      return <DataCatalogTable />
    case TabTypeStatic.Settings:
      return (
        <div className='flex w-full grow flex-col gap-6'>
          <Settings />
        </div>
      )
    case TabTypeStatic.NewChat:
      return (
        <div className='flex h-full w-full items-center'>
          <NewChat />
        </div>
      )
    case TabTypeStatic.IndustryReference:
      return <IndustryReferenceTable />

    // Chat tabs
    case TAB_TYPE_CHAT:
      return <Chat workflowId={tab.workflowId} />

    // Turn tabs
    case TAB_TYPE_TURN:
      return <TurnOutputTabContent tab={tab} />

    // Step tabs
    case TabTypeTurnStep.RecordedSession:
      return <RecordedSessionTabContent tab={tab} />
    case TabTypeTurnStep.LiveSession:
      return <LiveSessionTabContent tab={tab} />
    case TabTypeTurnStep.StepUpdates:
      return <StepUpdatesTabContent tab={tab} />

    // Database tabs
    case TAB_TYPE_DATABASE_SCHEMA:
      return <DatabaseSchemaTabContent databaseName={tab.databaseName} assetType={tab.assetType} />
    case TAB_TYPE_DATABASE_TABLE:
      return (
        <DatabaseTableTabContent
          databaseName={tab.databaseName}
          schemaName={tab.schemaName}
          assetType={tab.assetType}
        />
      )
    case TAB_TYPE_DATABASE_METADATA:
      return (
        <DatabaseMetadataTabContent
          databaseName={tab.databaseName}
          schemaName={tab.schemaName}
          tableName={tab.tableName}
          assetType={tab.assetType}
        />
      )
    case TAB_TYPE_DATA_GOVERNANCE_DASHBOARD:
      return <DataGovernanceDashboard tab={tab} />

    default:
      throw new Error(`Unhandled tab type: ${(tab as any).type}`)
  }
}
